import React, { useEffect, useReducer, useState } from 'react';
import styled from 'styled-components';
import { SCALE, VALIDATION_STATES } from '../../utils/constants';
import AddressGrid from '../AddressGrid/AddressGrid';
import LearnerInformationGrid from './LearnerInformationGrid';
import ThirdPartyInformationGrid from './ThirdPartyInformationGrid';
import Form from '../Form/Form';
import Password from '../Fields/Password';
import Phone from '../Fields/Phone';
import RequiredAsterisk from '../RequiredAsterisk/RequiredAsterisk';
import { Checkbox } from '../Checkbox/Checkbox';
import { parseDate } from '../../utils/dates';
import { Anchor, boulder, breakpoints, Button, dustGray, Heading5, Heading6, GridContainer, Loader } from '@parchment/atoms';
import { EditIcon } from '@parchment/icons';
import { useDeepCompareEffect } from '@react-hookz/web';
const noop = () => undefined;
const initialAddress = {
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: ''
};
const initialData = {
  firstName: '',
  middleName: '',
  lastName: '',
  organizationName: '',
  organizationType: '',
  title: '',
  dateOfBirth: '',
  highestLevelOfEducation: '',
  phone: '',
  address: initialAddress,
  password: ''
};
const INITIAL_VALIDATION_STATE = {
  learnerInformationData: {
    state: VALIDATION_STATES.neutral
  },
  thirdPartyInformationData: {
    state: VALIDATION_STATES.neutral
  },
  phone: {
    state: VALIDATION_STATES.neutral
  },
  addressData: {
    state: VALIDATION_STATES.neutral
  },
  password: {
    state: VALIDATION_STATES.neutral
  },
  retypePassword: {
    state: VALIDATION_STATES.neutral
  }
};
const VALIDATION_STATE_REDUCER = (prevState, updatedProperty) => ({
  ...prevState,
  ...updatedProperty
});
const LEARNER_INFORMATION_DATA_REDUCER = (data, updatedData) => ({
  ...data,
  ...updatedData
});
const THIRD_PARTY_INFORMATION_DATA_REDUCER = (data, updatedData) => ({
  ...data,
  ...updatedData
});
export default function RegistrationGrid(_ref) {
  let {
    formData = initialData,
    email,
    registrationType = 'LEARNER',
    loading = false,
    onChange = noop,
    onValidate = noop,
    onSubmit = noop,
    onEmailChange = noop
  } = _ref;
  const wrapper = /*#__PURE__*/React.createRef();
  const {
    firstName = '',
    middleName = '',
    lastName = '',
    organizationName = '',
    organizationType = '',
    title = '',
    dateOfBirth = '',
    highestLevelOfEducation = '',
    phone = '',
    address = initialAddress,
    password = ''
  } = formData;
  const [validationStates, setValidationStates] = useReducer(VALIDATION_STATE_REDUCER, INITIAL_VALIDATION_STATE);
  const [retypePassword, setRetypePassword] = useState('');
  const [requiresParentConsent, setRequiresParentConsent] = useState(false);
  const [isFormValidated, setIsFormValidated] = useState(false);
  const [hasParentConsent, setHasParentConsent] = useState(false);
  const [learnerInformationData, setLearnerInformationData] = useReducer(LEARNER_INFORMATION_DATA_REDUCER, {
    firstName,
    middleName,
    lastName,
    dateOfBirth,
    highestLevelOfEducation
  });
  const [thirdPartyInformationData, setThirdPartyInformationData] = useReducer(THIRD_PARTY_INFORMATION_DATA_REDUCER, {
    firstName,
    lastName,
    organizationName,
    organizationType,
    title
  });
  const personalInformationHeader = registrationType === 'LEARNER' ? 'ENTER YOUR PERSONAL INFORMATION' : 'YOUR ORGANIZATION INFORMATION';
  const contactInformationHeader = registrationType === 'LEARNER' ? 'ENTER YOUR CONTACT INFORMATION' : 'ORGANIZATION CONTACT INFORMATION';
  const passwordHeader = registrationType === 'LEARNER' ? 'CHOOSE A PASSWORD' : 'ACCOUNT INFORMATION';
  // can submit if all are true:
  //  * not loading
  //  * form is validated
  const canSubmit = !loading && isFormValidated;
  useEffect(() => {
    setLearnerInformationData({
      ...learnerInformationData,
      firstName: firstName,
      middleName: middleName,
      lastName: lastName,
      dateOfBirth: dateOfBirth,
      highestLevelOfEducation: highestLevelOfEducation
    });
  }, [firstName, middleName, lastName, dateOfBirth, highestLevelOfEducation]);
  useEffect(() => {
    const requiredStates = Object.fromEntries(Object.entries(validationStates).filter(_ref2 => {
      let [key] = _ref2;
      if (registrationType === 'LEARNER') {
        return !['thirdPartyInformationData'].includes(key);
      } else {
        return !['learnerInformationData'].includes(key);
      }
    }));

    // add parent consent to map of "states" that must be valid before we proceed
    if (requiresParentConsent) {
      requiredStates['parentConsent'] = {
        state: hasParentConsent ? 'success' : 'error'
      };
    }
    const states = Object.values(requiredStates).map(field => field.state);
    if (states.indexOf(VALIDATION_STATES.error) >= 0) {
      setIsFormValidated(false);
      return onValidate(VALIDATION_STATES.error);
    } else if (states.indexOf(VALIDATION_STATES.neutral) >= 0) {
      setIsFormValidated(false);
      return onValidate(VALIDATION_STATES.neutral);
    } else if (states.indexOf(VALIDATION_STATES.pending) >= 0) {
      setIsFormValidated(false);
      return onValidate(VALIDATION_STATES.pending);
    } else if (states.indexOf(VALIDATION_STATES.success) >= 0) {
      setIsFormValidated(true);
      return onValidate(VALIDATION_STATES.success);
    }
  }, [validationStates, hasParentConsent, requiresParentConsent, registrationType]);
  useDeepCompareEffect(() => {
    const dateOfBirthObj = parseDate(learnerInformationData.dateOfBirth);
    if (dateOfBirthObj.isValid) {
      const timeDiff = dateOfBirthObj.diffNow('years');
      setRequiresParentConsent(timeDiff.years > -18);
    }
    onChange({
      ...formData,
      ...learnerInformationData
    }, 'learnerInformationData');
  }, [learnerInformationData]);
  useDeepCompareEffect(() => {
    onChange({
      ...formData,
      ...thirdPartyInformationData
    }, 'thirdPartyInformationData');
  }, [thirdPartyInformationData]);
  const handleFormSubmit = () => {
    if (!canSubmit) {
      return false;
    }
    const data = {
      ...formData
    };
    if (requiresParentConsent) {
      data.parentConsent = hasParentConsent;
    }
    onSubmit(data);
  };
  const loadingLabel = loading ? /*#__PURE__*/React.createElement(Loader, {
    loading: true,
    loadingMessage: 'Loading...'
  }) : 'CREATE ACCOUNT & CONTINUE';
  return /*#__PURE__*/React.createElement("div", {
    ref: wrapper
  }, /*#__PURE__*/React.createElement(Form, {
    id: "RegistrationGrid",
    scale: SCALE.small,
    size: SCALE.small,
    autoComplete: "off"
  }, /*#__PURE__*/React.createElement(StyledInputSection, {
    className: "personal-information"
  }, /*#__PURE__*/React.createElement(StyledHeader, {
    className: "personal-information-header"
  }, personalInformationHeader), /*#__PURE__*/React.createElement(EmailSection, null, /*#__PURE__*/React.createElement(EmailText, null, email), /*#__PURE__*/React.createElement(ChangeEmailIcon, {
    id: "change-email",
    onClick: () => onEmailChange()
  }, /*#__PURE__*/React.createElement(EditIcon, {
    "aria-label": "edit email",
    role: "button"
  })), registrationType === 'THIRD_PARTY' ? /*#__PURE__*/React.createElement(ThirdPartyText, null, "Information about the learner is collected later.") : null), registrationType === 'LEARNER' ? /*#__PURE__*/React.createElement(LearnerInformationGrid, {
    fieldProps: learnerInformationData,
    onChange: value => setLearnerInformationData(value),
    onValidate: validationState => setValidationStates({
      learnerInformationData: {
        state: validationState
      }
    })
  }) : /*#__PURE__*/React.createElement(ThirdPartyInformationGrid, {
    fieldProps: thirdPartyInformationData,
    onChange: value => setThirdPartyInformationData(value),
    onValidate: validationState => setValidationStates({
      thirdPartyInformationData: {
        state: validationState
      }
    })
  })), /*#__PURE__*/React.createElement(StyledInputSection, {
    className: "contact-information"
  }, /*#__PURE__*/React.createElement(StyledHeader, {
    className: "contact-information-header"
  }, contactInformationHeader), /*#__PURE__*/React.createElement(Phone, {
    className: "phone-input",
    label: "Cell Phone",
    value: phone,
    onChange: value => onChange({
      ...formData,
      phone: value
    }, 'phone'),
    onValidate: validationState => setValidationStates({
      phone: {
        state: validationState
      }
    }),
    style: {
      'marginBottom': '5px'
    },
    required: true
  }), /*#__PURE__*/React.createElement("div", {
    className: "address-grid"
  }, /*#__PURE__*/React.createElement(AddressGrid, {
    address: address,
    onChange: value => onChange({
      ...formData,
      address: value
    }, 'address'),
    onValidate: validationState => setValidationStates({
      addressData: {
        state: validationState
      }
    }),
    required: true
  }))), /*#__PURE__*/React.createElement(StyledInputSection, {
    className: "create-password"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(StyledHeader, {
    className: "password-header"
  }, passwordHeader)), /*#__PURE__*/React.createElement(GridContainer, {
    className: "password-grid"
  }, /*#__PURE__*/React.createElement("input", {
    type: "email",
    id: "hiddenEmail",
    name: "identifier",
    tabIndex: -1,
    "aria-hidden": true,
    spellCheck: false,
    readOnly: true,
    autoComplete: "username",
    value: email,
    style: {
      'display': 'none'
    }
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    id: "identifierId",
    name: "identifierInput",
    value: email
  }), /*#__PURE__*/React.createElement(Password, {
    context: "create",
    value: password,
    internalLabel: true,
    onChange: value => onChange({
      ...formData,
      password: value
    }, 'password'),
    onValidate: validationState => setValidationStates({
      password: {
        state: validationState
      }
    }),
    width: "50%"
  }), /*#__PURE__*/React.createElement(Password, {
    context: "confirm",
    name: "passwordConfirm",
    label: "Retype Password",
    value: retypePassword,
    internalLabel: true,
    onChange: value => setRetypePassword(value),
    validators: [{
      message: 'Must Match Password',
      callback: value => value === password
    }],
    onValidate: validationState => setValidationStates({
      retypePassword: {
        state: validationState
      }
    }),
    width: "50%"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "submit-information"
  }, requiresParentConsent && /*#__PURE__*/React.createElement(ParentConsentSection, {
    className: "parent-consent"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    name: "parentConsent",
    isSelected: hasParentConsent,
    onChange: () => setHasParentConsent(!hasParentConsent)
  }, "I have my parents' or legal guardians' permission to create this Parchment account")), /*#__PURE__*/React.createElement(Button, {
    name: "submit",
    type: "submit",
    context: "primary",
    disabled: !canSubmit,
    onClick: handleFormSubmit,
    block: true
  }, loadingLabel), /*#__PURE__*/React.createElement(CenterAlignedRequiredWrapper, null, /*#__PURE__*/React.createElement(RequiredAsterisk, null)))));
}
const hasAnyPopulated = object => {
  return Object.values(object).some(value => {
    return !!(value && typeof value !== 'object');
  });
};
const StyledInputSection = /*#__PURE__*/styled.div.withConfig({
  displayName: "RegistrationGrid__StyledInputSection",
  componentId: "phjjcz-0"
})(["margin-bottom:20px;.phone-field{width:100%;margin-bottom:20px;.phone-input span > div{border-radius:5px;}}.address-grid{margin-top:20px;}@media (min-width:", "){.phone-field{width:33%;}}"], breakpoints.mediumMin);
const StyledHeader = /*#__PURE__*/styled(Heading5).withConfig({
  displayName: "RegistrationGrid__StyledHeader",
  componentId: "phjjcz-1"
})(["font-size:16px;font-weight:600;padding:10px 0;@media (min-width:", "){font-size:18px;}"], breakpoints.mediumMin);
const EmailSection = /*#__PURE__*/styled.div.withConfig({
  displayName: "RegistrationGrid__EmailSection",
  componentId: "phjjcz-2"
})(["display:flex;align-items:center;flex-wrap:wrap;margin-bottom:15px;"]);
const EmailText = /*#__PURE__*/styled(Heading6).withConfig({
  displayName: "RegistrationGrid__EmailText",
  componentId: "phjjcz-3"
})(["font-size:14px;font-weight:bold;color:", ";line-height:20px;"], boulder);
const ChangeEmailIcon = /*#__PURE__*/styled(Anchor).withConfig({
  displayName: "RegistrationGrid__ChangeEmailIcon",
  componentId: "phjjcz-4"
})(["margin-left:10px;"]);
const ThirdPartyText = /*#__PURE__*/styled.p.withConfig({
  displayName: "RegistrationGrid__ThirdPartyText",
  componentId: "phjjcz-5"
})(["font-size:14px;font-weight:600;font-style:italic;color:", ";margin:0;line-height:20px;@media (min-width:", "){margin:0 0 0 auto;}"], dustGray, breakpoints.mediumMin);
const ParentConsentSection = /*#__PURE__*/styled.div.withConfig({
  displayName: "RegistrationGrid__ParentConsentSection",
  componentId: "phjjcz-6"
})(["margin-bottom:30px;"]);
const CenterAlignedRequiredWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "RegistrationGrid__CenterAlignedRequiredWrapper",
  componentId: "phjjcz-7"
})(["display:flex;justify-content:center;padding-top:30px;"]);